import type { PropsWithChildren } from "react";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import EditableField from "../../../../viewer/common/fields/editable-field/EditableField.tsx";
import type { Maybe } from "@entrilia/shared/types.ts";
import { selectConditions, selectTabularFields } from "../../../../../store/selectors/fieldsStateSelectors";
import { isCustomIdUnique } from "./utils/isCustomIdUnique.ts";
import { sanitizeCustomId } from "./utils/sanitizeCustomId.ts";

interface CustomIdProps {
  customId?: Maybe<string>;
  defaultId: string;
  readonly?: boolean;
  updateCustomId: ((customId: string) => void) | undefined;
  onEditMode?: (isEditing: boolean) => void;
}
const CustomId = ({
  customId,
  defaultId,
  readonly,
  updateCustomId,
  onEditMode,
  children,
}: PropsWithChildren<CustomIdProps>) => {
  const fieldsAreaValues = useSelector(selectTabularFields);
  const conditionsAreaValues = useSelector(selectConditions);

  const [validationError, setValidationError] = useState<string>();

  const handleEditModeChanged = (isEditing: boolean) => {
    if (isEditing === false) {
      setValidationError(undefined);
    }
    onEditMode?.(isEditing);
  };

  const validate = useCallback(
    (customId: string) => {
      if (isCustomIdUnique(defaultId, customId, fieldsAreaValues, conditionsAreaValues)) {
        setValidationError(undefined);
        return true;
      }
      setValidationError("Field Id should be unique");
      return false;
    },
    [defaultId, fieldsAreaValues, conditionsAreaValues]
  );

  return (
    <EditableField
      customFieldValue={customId}
      originalFieldValue={defaultId}
      updateCustomFieldValue={updateCustomId}
      sx={{ pt: 0 }}
      onEditMode={handleEditModeChanged}
      readonly={readonly}
      label="Id"
      defaultLabel="Default Id"
      resetToDefaultText="Reset to Default Id"
      validationError={validationError}
      validateInput={validate}
      sanitizeInput={sanitizeCustomId}
    >
      {children}
    </EditableField>
  );
};

export default CustomId;
